import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal } from 'react-bootstrap-5';
import snakecaseKeys from 'snakecase-keys';
import { isEmpty } from 'lodash';

import Errors from 'components/form/errors';

import { createList } from 'helpers/clarity_api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';

const PN_CONTACT_FREQUENCY = [30, 45, 60, 90, 120];

const DEFAULT_CALL_LIST = {
  name: '',
  description: '',
  contactFrequency: PN_CONTACT_FREQUENCY[0]
};

export default function CreateListModal({ assembleCallListItems, show, onClose }) {
  const [formData, setFormData] = useState({ ...DEFAULT_CALL_LIST });
  const [errors, setErrors] = useState();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (saved) setTimeout(handleClose, 3000);
  }, [saved]);

  useEffect(() => {
    if (show) setSaved(false);
  }, [show]);

  useEffect(()=>{
    if (show) {
      setFormData({ ...DEFAULT_CALL_LIST });
      setErrors(null);
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleSave = () => {
    setSaving(true);

    (async () => {
      const callListItems = await assembleCallListItems();
      const body = { ...formData, callListItemsAttributes: callListItems };

      createList(snakecaseKeys(body))
        .then((response) => {
          if (response.status !== 201) {
            throw response?.data?.errors || ['Something went wrong'];
          }

          setSaved(true);
        })
        .catch(errors => setErrors(errors))
        .finally(() => setSaving(false));
    })();
  };

  return (
    <Modal
      centered
      show={show}
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>New List</Modal.Title>
      </Modal.Header>

      {saved && (
        <Modal.Body>
          <div className="text-primary animate__animated animate__fadeIn text-center my-3" style={{ fontSize: '1rem' }}>
            <FontAwesomeIcon className="me-2" icon={faCheckCircle} />
            Saved to "{formData.name}"
          </div>
        </Modal.Body>
      )}
      {!saved && (
        <React.Fragment>
          <Modal.Body>
            <Errors errors={errors} />

            <Form>
              <Form.Group className="mb-3" controlId="formCallListName">
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" type="text" value={formData.name} onChange={handleChange}/>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formCallListDescription">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group controlId="formCallListContactFrequency">
                <Form.Label>Contact Frequency</Form.Label>
                <Form.Select
                  aria-label="Contact Frequency"
                  name="contactFrequency"
                  value={formData.contactFrequency}
                  onChange={handleChange}
                >
                  {PN_CONTACT_FREQUENCY.map(freq => (
                    <option key={freq} value={freq}>
                      {freq}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="link" onClick={handleClose}>Cancel</Button>
            <Button disabled={saving || isEmpty(formData.name)} variant="primary" onClick={handleSave}>
              {saving ? 'Saving...' : 'Save List'}
            </Button>
          </Modal.Footer>
        </React.Fragment>
      )}
    </Modal>
  );
}

CreateListModal.propTypes = {
  assembleCallListItems: PropTypes.func.isRequired,
  show: PropTypes.bool,
  onClose: PropTypes.func
};
